import React, { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown, CircleSlash, RotateCw, Search, TriangleAlert, X } from 'lucide-react';
import { Button, Card, Pill } from '../../../components/ui';
import { RUN_LABEL, durationLabel, plural } from '../format';
import type { RunLane, RunLogEntry, RunView } from '../types';

const laneTone: Record<RunLane['status'], string> = {
  queued: 'border-neutral-200 text-neutral-400',
  running: 'border-primary-200 bg-primary-50 text-primary-700',
  done: 'border-neutral-200 bg-neutral-50 text-neutral-600',
  failed: 'border-primary-200 text-primary-700',
  skipped: 'border-neutral-200 text-neutral-400',
};

const LEVEL_MARK: Record<RunLogEntry['level'], React.ReactNode> = {
  info: <Search className="h-3 w-3 text-neutral-300" strokeWidth={1.75} />,
  step: <span className="block h-1 w-1 rounded-full bg-ink" />,
  warn: <TriangleAlert className="h-3 w-3 text-amber-500" strokeWidth={1.75} />,
  error: <TriangleAlert className="h-3 w-3 text-primary-600" strokeWidth={1.75} />,
  success: <Check className="h-3 w-3 text-emerald-600" strokeWidth={2} />,
};

/** Numbers worth reading, in the order they make sense */
const STAT_ORDER: Array<[string, string]> = [
  ['entities', 'entities found'],
  ['new', 'new'],
  ['actsFound', 'new acts found'],
  ['actsRead', 'acts read'],
  ['actsUnread', 'acts not readable'],
  ['inPost', 'holders in post'],
  ['proposed', 'changes proposed'],
  ['firstNominations', 'first nominations found'],
  ['matched', 'matched to records'],
  ['toMerge', 'to merge'],
  ['toCreate', 'to create'],
  ['outside', 'outside the structure'],
  ['searches', 'searches'],
  ['sourcesChecked', 'sources checked'],
  ['deadSources', 'dead links dropped'],
  ['stale', 'gone or not found again'],
  ['untied', 'wrong records untied'],
  ['keptApart', 'kept apart after a check'],
  ['calls', 'model calls'],
];

/** Where a waiting run sits, said the way a person would say it */
const positionLabel = (position: number | null): string =>
  position === 1 ? 'Next' : position ? `${position} in line` : 'Waiting';

const Elapsed: React.FC<{ from: string; to?: string | null; running: boolean }> = ({ from, to, running }) => {
  const [, tick] = useState(0);
  useEffect(() => {
    if (!running) return;
    const timer = setInterval(() => tick((value) => value + 1), 1000);
    return () => clearInterval(timer);
  }, [running]);
  return <span className="tabular-nums">{durationLabel(from, to)}</span>;
};

/**
 * What the research is doing, while it does it.
 *
 * Long research is mostly waiting, and waiting without being told what is
 * happening feels broken — so every search, every branch and every source
 * check is said out loud, and the whole thing can be stopped.
 */
export const RunConsole: React.FC<{
  run: RunView;
  /** Accepted and waiting their turn, in order */
  queue?: RunView[];
  onStop?: () => void;
  onDismiss?: () => void;
  /** Offered when a run stopped before it finished */
  onResume?: () => void;
  /** Takes one off the queue before it starts */
  onRemove?: (id: string) => void;
  canStop?: boolean;
  className?: string;
}> = ({ run, queue = [], onStop, onDismiss, onRemove, onResume, canStop = true, className = '' }) => {
  const running = run.status === 'running';
  const waiting = run.status === 'queued';
  const [open, setOpen] = useState(true);
  const logRef = useRef<HTMLDivElement>(null);
  const lines = open ? run.log.slice(-40) : run.log.slice(-3);

  useEffect(() => {
    const element = logRef.current;
    if (element) element.scrollTop = element.scrollHeight;
  }, [run.log.length, open]);

  const percent = waiting
    ? 0
    : run.progress.total
      ? Math.min(100, Math.round((run.progress.done / run.progress.total) * 100))
      : running
        ? 8
        : 100;
  const stats = STAT_ORDER.filter(([key]) => (run.stats?.[key] ?? 0) > 0).map(
    ([key, label]) => `${run.stats[key]} ${label}`,
  );

  return (
    <Card className={`overflow-hidden ${className}`}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3">
        <span className="relative flex h-2 w-2 flex-shrink-0" aria-hidden="true">
          {running && (
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary-500 opacity-60" />
          )}
          <span
            className={`relative inline-flex h-2 w-2 rounded-full ${
              running
                ? 'bg-primary-600'
                : waiting
                  ? 'bg-amber-500'
                  : run.status === 'done'
                  ? 'bg-emerald-500'
                  : run.status === 'failed'
                    ? 'bg-primary-600'
                    : 'bg-neutral-300'
            }`}
          />
        </span>
        {/* a minimum width, so on a phone the status and buttons wrap under the title instead of squeezing it */}
        <div className="min-w-[12rem] flex-1">
          <p className="truncate text-[13px] text-ink">{run.title}</p>
          <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[12px] text-neutral-400">
            <span>{RUN_LABEL[run.kind]}</span>
            <span>·</span>
            {waiting ? (
              <span>{positionLabel(run.queuePosition)}</span>
            ) : (
              <Elapsed from={run.startedAt} to={run.finishedAt} running={running} />
            )}
            {run.progress.total > 0 && (
              <>
                <span>·</span>
                <span className="tabular-nums">
                  {run.progress.done}/{run.progress.total}
                </span>
              </>
            )}
            {run.providers.length > 0 && (
              <>
                <span>·</span>
                <span className="truncate" title={run.providers.join(', ')}>
                  {run.providers[0]}
                </span>
              </>
            )}
          </p>
        </div>
        {waiting && <Pill tone="amber">Waiting</Pill>}
        {!running && !waiting && (
          <Pill tone={run.status === 'done' ? 'green' : run.status === 'failed' ? 'red' : 'neutral'}>
            {run.status === 'done'
              ? 'Done'
              : run.status === 'failed'
                ? 'Failed'
                : run.status === 'cancelled'
                  ? 'Stopped'
                  : 'Interrupted'}
          </Pill>
        )}
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          className="inline-flex h-7 items-center gap-1 rounded-full px-2 text-[12px] text-neutral-500 transition-colors hover:text-ink"
          aria-expanded={open}
        >
          {open ? 'Hide' : 'Show'}
          <ChevronDown
            className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`}
            strokeWidth={1.75}
          />
        </button>
        {(running || waiting) && canStop && onStop && (
          <Button size="xs" variant="secondary" onClick={onStop}>
            <CircleSlash className="h-3.5 w-3.5" strokeWidth={1.75} />
            {waiting ? 'Remove' : 'Stop'}
          </Button>
        )}
        {!running && !waiting && onResume && run.status !== 'done' && (
          <Button size="xs" variant="secondary" onClick={onResume} title="Carry on from what it had already found">
            <RotateCw className="h-3.5 w-3.5" strokeWidth={1.75} />
            Resume
          </Button>
        )}
        {!running && !waiting && onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            aria-label="Hide this run"
            className="inline-flex h-7 w-7 items-center justify-center rounded-full text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-ink"
          >
            <X className="h-4 w-4" strokeWidth={1.75} />
          </button>
        )}
      </div>

      <div className="h-px w-full bg-neutral-100">
        <div
          className={`h-px bg-primary-600 transition-all duration-700 ease-out ${running ? '' : 'opacity-40'}`}
          style={{ width: `${percent}%` }}
        />
      </div>

      {run.lanes.length > 1 && open && (
        <div className="flex flex-wrap gap-1.5 px-4 pt-3">
          {run.lanes.map((lane) => (
            <span
              key={lane.key}
              title={lane.detail ?? undefined}
              className={`inline-flex max-w-[220px] items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11.5px] ${laneTone[lane.status]}`}
            >
              {lane.status === 'running' && (
                <span className="h-1.5 w-1.5 flex-shrink-0 animate-pulse rounded-full bg-primary-600" />
              )}
              {lane.status === 'done' && <Check className="h-3 w-3 flex-shrink-0" strokeWidth={2} />}
              {lane.status === 'failed' && (
                <TriangleAlert className="h-3 w-3 flex-shrink-0" strokeWidth={1.75} />
              )}
              <span className="truncate">{lane.label}</span>
              {typeof lane.count === 'number' && lane.count > 0 && (
                <span className="tabular-nums opacity-60">{lane.count}</span>
              )}
            </span>
          ))}
        </div>
      )}

      <div
        ref={logRef}
        className={`space-y-1 overflow-y-auto px-4 py-3 ${open ? 'max-h-52' : 'max-h-20'}`}
        aria-live="polite"
      >
        {lines.length === 0 && (
          <p className="text-[12.5px] text-neutral-400">Starting…</p>
        )}
        {lines.map((entry, index) => (
          <p
            key={`${entry.at}-${index}`}
            className={`flex animate-fade-up items-start gap-2 text-[12.5px] leading-snug ${
              entry.level === 'error'
                ? 'text-primary-700'
                : entry.level === 'warn'
                  ? 'text-amber-700'
                  : entry.level === 'step' || entry.level === 'success'
                    ? 'text-ink'
                    : 'text-neutral-500'
            }`}
          >
            <span className="mt-1.5 flex-shrink-0">{LEVEL_MARK[entry.level]}</span>
            <span className="min-w-0 break-words">{entry.message}</span>
          </p>
        ))}
      </div>

      {queue.length > 0 && (
        <div className="border-t border-neutral-200/70">
          <p className="px-4 pb-1 pt-2.5 text-[11px] uppercase tracking-[0.08em] text-neutral-400">
            Waiting · {queue.length}
          </p>
          <ul className="divide-y divide-neutral-200/70">
            {queue.map((item, index) => (
              <li key={item.id} className="flex items-center gap-2.5 px-4 py-2">
                <span className="w-3 flex-shrink-0 text-[12px] tabular-nums text-neutral-400">
                  {item.queuePosition ?? index + 1}
                </span>
                <span className="min-w-0 flex-1 truncate text-[13px] text-ink">{item.title}</span>
                <span className="flex-shrink-0 text-[12px] text-neutral-400">{RUN_LABEL[item.kind]}</span>
                {onRemove && (
                  <button
                    type="button"
                    onClick={() => onRemove(item.id)}
                    aria-label={`Take "${item.title}" off the queue`}
                    title="Take off the queue"
                    className="inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-ink"
                  >
                    <X className="h-4 w-4" strokeWidth={1.75} />
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {(stats.length > 0 || run.error) && (
        <div className="border-t border-neutral-200/70 px-4 py-2.5">
          {run.error ? (
            <p className="text-[12.5px] text-primary-700">{run.error}</p>
          ) : (
            <p className="text-[12px] text-neutral-500">{stats.join(' · ')}</p>
          )}
        </div>
      )}
    </Card>
  );
};

/** One line about a finished run, for the history list */
export const RunRow: React.FC<{ run: RunView; onOpen: () => void }> = ({ run, onOpen }) => {
  const entities = run.stats?.entities ?? 0;
  const proposed = run.stats?.proposed ?? 0;
  const detail = [
    entities ? plural(entities, 'entity', 'entities') : null,
    run.stats?.actsRead ? plural(run.stats.actsRead, 'act') + ' read' : null,
    proposed ? plural(proposed, 'change') : null,
    run.stats?.calls ? plural(run.stats.calls, 'call') : null,
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-neutral-50"
    >
      <span
        className={`h-1.5 w-1.5 flex-shrink-0 rounded-full ${
          run.status === 'done'
            ? 'bg-emerald-500'
            : run.status === 'running'
              ? 'animate-pulse bg-primary-600'
              : run.status === 'failed'
                ? 'bg-primary-600'
                : 'bg-neutral-300'
        }`}
      />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[13px] text-ink">{run.title}</span>
        <span className="block truncate text-[12px] text-neutral-400">
          {new Date(run.startedAt).toLocaleString(undefined, {
            day: 'numeric',
            month: 'short',
            hour: '2-digit',
            minute: '2-digit',
          })}
          {detail ? ` · ${detail}` : ''}
        </span>
      </span>
      <span className="flex-shrink-0 text-[12px] tabular-nums text-neutral-400">
        {durationLabel(run.startedAt, run.finishedAt)}
      </span>
    </button>
  );
};
